import { c as _c } from "react/compiler-runtime";
import { isProjectArchivingState } from "@custom-types/type-guards";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { NotFoundPage } from "@pages/not-found-page";
import { ProjectsPage } from "@pages/projects/projects-page";
import { useAppParams } from "@router/router-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { isValidProjectOrCompanyId } from "@utils/string-utils";
import { useEffect } from "react";

/**
 * Simple component used to validate the route param from the routes ".../projects/:param".
 *
 * We have the problem that the route param after projects route "/projects/:param"
 * can either be "active", "archived", a valid project ID or some other string.
 * That's why here we have to validate first if the param is a valid project status.
 * If it's a valid status then we allow access the projects page. If not we validate if
 * the param is a valid project ID, if it is we navigate to the project details route.
 * Finally if the param is not a valid status or project ID we render the "Not found" page.
 *
 * Idea taken from the official documentation regarding param validation:
 * https://reactrouter.com/en/main/start/faq#what-happened-to-regexp-routes-paths
 */
export function ValidateProjectStatus() {
  const $ = _c(9);
  const {
    navigateToProjectDetail
  } = useAppNavigation();
  const {
    projectStatus
  } = useAppParams();
  const companyId = useCompanyId();
  let t0;
  let t1;
  if ($[0] !== companyId || $[1] !== navigateToProjectDetail || $[2] !== projectStatus) {
    t0 = () => {
      if (projectStatus && !isProjectArchivingState(projectStatus) && isValidProjectOrCompanyId(projectStatus)) {
        navigateToProjectDetail({
          companyId,
          projectId: projectStatus
        });
      }
    };
    t1 = [companyId, navigateToProjectDetail, projectStatus];
    $[0] = companyId;
    $[1] = navigateToProjectDetail;
    $[2] = projectStatus;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  if (isProjectArchivingState(projectStatus)) {
    let t2;
    if ($[5] !== companyId || $[6] !== projectStatus) {
      t2 = <ProjectsPage companyId={companyId} projectStatus={projectStatus} />;
      $[5] = companyId;
      $[6] = projectStatus;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  if (projectStatus && !isProjectArchivingState(projectStatus) && !isValidProjectOrCompanyId(projectStatus)) {
    let t2;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <NotFoundPage />;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
}